import {
  AdditiveBlending,
  BoxGeometry,
  BufferGeometry,
  Camera,
  CatmullRomCurve3,
  Color,
  DoubleSide,
  Float32BufferAttribute,
  Group,
  IcosahedronGeometry,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  NormalBlending,
  Object3D,
  PointLight,
  Points,
  RingGeometry,
  ShaderMaterial,
  SphereGeometry,
  TorusGeometry,
  TubeGeometry,
  CylinderGeometry,
  Vector3,
  Vector4,
  BackSide,
  InstancedMesh,
  Matrix4,
  CircleGeometry,
  PlaneGeometry,
  InstancedBufferAttribute,
  Sprite,
  SpriteMaterial,
  CanvasTexture,
} from "three";
import {
  BALL_RADIUS,
  launchVelocity,
  type HoleLayout,
  type Planet,
  type Target,
} from "@shared/physics";
import { mulberry32 } from "@shared/rng";
import { currentTheme } from "../theme";

// v7 types arrive in a concurrent branch; alias the optional fields so the
// code compiles in this worktree alone and still merges cleanly with the
// physics agent's changes.
interface ArenaCapsuleSeg {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  radius: number;
}
interface GateLike {
  cx: number;
  cz: number;
  nx: number;
  nz: number;
  halfWidth: number;
  thickness: number;
  closedSeconds: number;
  openSeconds: number;
  phase: number;
}

// Bumped from 24 → 64 so Cosmic Runner's mile-long wavy corridor
// can be sampled at TRACK_STEP=9 over a 540u visible window without
// hitting the limit. Each capsule adds 2 vec4 uniforms (~16 bytes
// per slot) and one loop iteration in the SDF — 64 slots = ~1KB
// uniforms + a 64-iteration loop in the fragment shader. Modern
// mobile GPUs are fine with this; iOS Safari documented limit is
// 256 vec4 uniforms in fragment shaders so we're well under.
const MAX_CAPSULES_IN_SHADER = 64;

export interface HoleVisuals {
  root: Group;
  ball: Mesh;
  trail: Line;
  trailGeom: BufferGeometry;
  aimGuide: Line;
  aimGeom: BufferGeometry;
  trajectoryPreview: InstancedMesh;
  trajectoryGeom: BufferGeometry;
  powerRing: Mesh;
  targetHalo: Mesh;
  targetDisk: Mesh;
  targetDiskMat: ShaderMaterial;
  targetParticles: Points;
  /** Extra black-hole shader materials (photon ring, lens ring) that
   *  need `uTime` ticked each frame. Parallel-updated alongside
   *  targetHalo / targetDiskMat in game.ts. */
  targetExtraMats: ShaderMaterial[];
  /** Billboard torus that wraps the horizon like a lensed halo.
   *  game.ts reorients this each frame with `lookAt(camera.position)`
   *  so the ring always faces the player. */
  targetLensRing: Mesh;
  planets: Mesh[];
  /** Parallel to `planets` — the parent Group of each body, used by
   *  game.ts to re-position orbiting planets each frame. */
  planetRoots: Group[];
  planetRings: Mesh[]; // parallel to planets; may contain null-ish meshes
  /** Flat ground disks pulsing beneath each planet — reads as the
   *  "gravity well" strength so players can plan their slingshots. */
  gravityWells: Mesh[];
  /** Paired wormhole meshes, in the same index order as
   *  hole.wormholes so animation + color matching is trivial. */
  wormholes: Group[];
  /** Slowly orbiting perimeter dust — game.ts ticks it each frame. */
  boundaryParticles: BoundaryParticles;
  /** The orbital-chart material — shared uniforms between the disc,
   *  boundary ring, and shimmer cylinder. game.ts must write
   *  `uBallPos` each frame so the proximity grid follows the ball. */
  boundaryMat: ShaderMaterial;
  /** Per-gate render handles (v7). Empty if the hole has no gates.
   *  game.ts animates uClosed each frame using gateIsClosed(). */
  gates: GateRender[];
  /** Wind streamer particle system (v7). Null if no windAccel on hole. */
  windStreamers: WindStreamers | null;
  /** Comet hazard render handles (v11). Empty if the hole has no
   *  comets. game.ts ticks the head position and tail fade each frame
   *  using hole-local sim time. */
  comets: CometRender[];
  dispose(): void;
}

/** One rendered gate: the bar mesh + its shader material so we can write
 *  the closed/open uniform each frame. */
export interface GateRender {
  gate: GateLike;
  root: Group;
  barMat: ShaderMaterial;
  /** Smoothed 0..1 indicating crossfaded closed state. */
  closedCrossfade: number;
}

interface CometLike {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  period: number;
  phase: number;
  radius: number;
}

/** One rendered comet hazard: the head group (core + halo) + a
 *  static track line showing where the comet patrols. game.ts
 *  animates the head each frame using the cometPosAt helper from
 *  shared/physics. */
export interface CometRender {
  comet: CometLike;
  /** Root object — setting `.position.x/z` moves both core + halo. */
  head: Object3D;
  headMat: ShaderMaterial;
  track: Line;
}

export interface WindStreamers {
  points: Points;
  update(t: number): void;
}

export interface BoundaryParticles {
  group: Group;
  update(t: number): void;
}

export function buildHoleVisuals(hole: HoleLayout): HoleVisuals {
  const root = new Group();
  const rng = mulberry32(hole.seed);

  // NO arena floor — we are in space. The bounds exist in physics but not visually.

  // Planets.
  const planets: Mesh[] = [];
  const planetRoots: Group[] = [];
  const planetRings: Mesh[] = [];
  const gravityWells: Mesh[] = [];
  for (let i = 0; i < hole.planets.length; i++) {
    const p = hole.planets[i]!;
    const group = buildPlanet(p, rng, i);
    root.add(group.root);
    planets.push(group.body);
    planetRoots.push(group.root);
    if (group.ring) planetRings.push(group.ring);
    // Flat gravity-well disk on the ground — reach ∝ √strength so
    // players can eyeball slingshots. Added to root (not the planet
    // group) so it lies on the world's xz plane regardless of the
    // planet's tilt.
    const well = buildGravityWell(p);
    root.add(well);
    gravityWells.push(well);
    // Orbit path indicator for moving planets — dashed ring at y=0.02
    // showing the circular track the body will sweep during a shot.
    // Purely visual; physics doesn't reference it.
    if (p.orbit) {
      const orbitRing = buildOrbitPathRing(p);
      root.add(orbitRing);
    }
  }

  // Wormhole pair — only some holes have them (see course.ts escalation).
  const wormholes: Group[] = [];
  if (hole.wormholes && hole.wormholes.length === 2) {
    const palettes: [string, string][] = [
      ["#8ad5ff", "#7cf5ff"], // entry — cyan
      ["#c987ff", "#ff9ed8"], // partner — magenta
    ];
    for (let i = 0; i < hole.wormholes.length; i++) {
      const w = hole.wormholes[i]!;
      const pal = palettes[i % palettes.length]!;
      const g = buildWormhole(w.cx, w.cz, w.radius, pal[0], pal[1]);
      root.add(g);
      wormholes.push(g);
    }
  }

  // Target (black hole).
  const targetGroup = buildBlackHole(hole.target);
  root.add(targetGroup.root);
  const targetHalo = targetGroup.halo;
  const targetDisk = targetGroup.disk;
  const targetDiskMat = targetGroup.diskMat;
  const targetParticles = targetGroup.particles;
  const targetExtraMats = targetGroup.extraMats;
  const targetLensRing = targetGroup.lensRing;

  // Tee ring.
  root.add(buildTeeRing(hole.tee.x, hole.tee.z));

  // Ball. Glow is a child of the body so moving body.position moves both.
  const ball = buildBall();
  ball.position.set(hole.tee.x, BALL_RADIUS + 0.06, hole.tee.z);
  root.add(ball);

  // Plasma trail (tube-ish line, thicker). We use a Line with additive blending
  // for now — WebGL1 doesn't support line width, so we layer 3 lines of
  // slightly offset Y to fake thickness without blowing perf.
  const trailGeom = new BufferGeometry();
  trailGeom.setAttribute(
    "position",
    new Float32BufferAttribute(new Float32Array(MAX_TRAIL_POINTS * 3), 3),
  );
  trailGeom.setAttribute(
    "color",
    new Float32BufferAttribute(new Float32Array(MAX_TRAIL_POINTS * 3), 3),
  );
  trailGeom.setDrawRange(0, 0);
  const trail = new Line(
    trailGeom,
    new LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 1.0,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  root.add(trail);

  // Aim guide (dashed line from ball in aim direction while dragging).
  const aimGeom = new BufferGeometry();
  aimGeom.setAttribute(
    "position",
    new Float32BufferAttribute(new Float32Array(2 * 3), 3),
  );
  const aimGuide = new Line(
    aimGeom,
    new LineBasicMaterial({
      color: new Color("#ffffff"),
      transparent: true,
      opacity: 0.0,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  root.add(aimGuide);

  // Trajectory preview — glowing predicted path, per-vertex alpha fades from head to tail.
  const trajectoryGeom = new CircleGeometry(0.12, 12);
  trajectoryGeom.rotateX(-Math.PI / 2); // face upwards
  const matTraj = new MeshBasicMaterial({
    color: new Color("#ffffff"),
    transparent: true,
    opacity: 0,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  const trajectoryPreview = new InstancedMesh(trajectoryGeom, matTraj, PREVIEW_SAMPLES);
  trajectoryPreview.instanceMatrix.setUsage(35048); // DYNAMIC_DRAW
  // Add color attribute for instances
  const colTraj = new Float32Array(PREVIEW_SAMPLES * 3);
  for(let i=0; i<PREVIEW_SAMPLES; i++) {
    colTraj[i*3] = 1; colTraj[i*3+1] = 1; colTraj[i*3+2] = 1;
  }
  trajectoryPreview.instanceColor = new InstancedBufferAttribute(colTraj, 3);
  trajectoryPreview.count = 0;
  root.add(trajectoryPreview);

  // Power ring — flat ring that grows around the ball while charging.
  const powerRing = new Mesh(
    new RingGeometry(0.7, 0.82, 48),
    new MeshBasicMaterial({
      color: new Color("#7cf5ff"),
      transparent: true,
      opacity: 0.0,
      side: DoubleSide,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  powerRing.rotation.x = -Math.PI / 2;
  powerRing.position.set(hole.tee.x, 0.02, hole.tee.z);
  root.add(powerRing);

  // Warm key light up high — makes planet faces read.
  const key = new PointLight("#ffd9a8", 2.0, 200, 1.4);
  key.position.set(20, 60, 10);
  root.add(key);
  // Cool fill.
  const fill = new PointLight("#6aa6ff", 1.0, 200, 1.4);
  fill.position.set(-30, 30, -20);
  root.add(fill);

  // Arena surface — instrument-panel aesthetic (Day 9 orbital chart).
  // Replaces the old "floor" + tall wall-cylinder in favor of a single
  // translucent sensor-display disc + a thin glowing boundary ring +
  // a very faint shimmer cylinder for 3D presence. Everything is
  // clipped in the shader to either the ellipse OR a capsule-chain
  // SDF (when `hole.arenaSegments` is set), so the proximity grid
  // shows up *wherever* the surface is visible — no more UV mismatch.
  const arenaR =
    hole.arenaRadius ??
    Math.min(hole.bounds.maxX - hole.bounds.minX, hole.bounds.maxZ - hole.bounds.minZ) / 2;
  const arenaRX = hole.arenaRadiusX ?? arenaR;
  const arenaRZ = hole.arenaRadiusZ ?? arenaR;
  const theme = currentTheme();
  const accent = new Color(theme.accent);

  // Capsule segments arrive from the v7 generator as optional array.
  // When absent we behave exactly as before (ellipse).
  const arenaSegments: ArenaCapsuleSeg[] | undefined =
    (hole as unknown as { arenaSegments?: ArenaCapsuleSeg[] }).arenaSegments;
  const hasCapsules = !!(arenaSegments && arenaSegments.length > 0);

  // Pack capsule data for the shader. We reserve a fixed-size array
  // (MAX_CAPSULES_IN_SHADER) to keep the uniform layout stable across
  // holes; the `uNumCaps` uniform says how many are "live". Each cap
  // is stored as two vec4s so the shader can index without variable-
  // length arrays (not supported on WebGL1):
  //   uCapsA[i] = (ax, az, bx, bz)
  //   uCapsB[i] = (radius, 0, 0, 0)
  const capsAVec4: Vector4[] = [];
  const capsBVec4: Vector4[] = [];
  for (let i = 0; i < MAX_CAPSULES_IN_SHADER; i++) {
    capsAVec4.push(new Vector4(0, 0, 0, 0));
    capsBVec4.push(new Vector4(0, 0, 0, 0));
  }
  let numCaps = 0;
  if (hasCapsules) {
    const caps = arenaSegments!.slice(0, MAX_CAPSULES_IN_SHADER);
    numCaps = caps.length;
    for (let i = 0; i < numCaps; i++) {
      const c = caps[i]!;
      capsAVec4[i]!.set(c.ax, c.az, c.bx, c.bz);
      capsBVec4[i]!.set(c.radius, 0, 0, 0);
    }
  }

  // Shared uniforms for disc + boundary ring + shimmer cylinder so the
  // single uBallPos write in the frame loop drives all three surfaces.
  const boundaryUniforms: Record<string, { value: unknown }> = {
    uTime: { value: 0 },
    uBallPos: { value: new Vector3(hole.tee.x, 0, hole.tee.z) },
    uAccent: { value: accent.clone() },
    uArenaRX: { value: arenaRX },
    uArenaRZ: { value: arenaRZ },
    uNumCaps: { value: numCaps },
    uCapsA: { value: capsAVec4 },
    uCapsB: { value: capsBVec4 },
    // 1.0 = use capsule SDF, 0.0 = ellipse
    uUseCaps: { value: hasCapsules ? 1 : 0 },
  };

  // The orbital-chart disc material — gradient fill + static concentric
  // rings + proximity grid, all baked together.
  const boundaryMat = buildOrbitalChartMaterial(boundaryUniforms, false);

  // A surface big enough to cover every capsule's bounding rectangle
  // (including corners). The shader clips to the arena SDF so only the
  // real arena shape gets painted — but the MESH must cover every
  // potential pixel or we'll see hard polygon edges where the chart is
  // "missing" (what happened when this used a scaled unit circle: the
  // inscribed ellipse left the corner regions uncovered and the
  // multi-path wavy river's crossing points clipped through).
  const discHalfX = Math.max(
    arenaRX,
    ...(arenaSegments?.map((c) => Math.max(Math.abs(c.ax), Math.abs(c.bx)) + c.radius) ?? [0]),
  ) + 2;
  const discHalfZ = Math.max(
    arenaRZ,
    ...(arenaSegments?.map((c) => Math.max(Math.abs(c.az), Math.abs(c.bz)) + c.radius) ?? [0]),
  ) + 2;
  const discGeom = new PlaneGeometry(discHalfX * 2, discHalfZ * 2, 1, 1);
  const disc = new Mesh(discGeom, boundaryMat);
  disc.rotation.x = -Math.PI / 2;
  disc.position.y = 0.02;
  root.add(disc);

  // Thin glowing boundary ring at y≈0.05 along the arena perimeter.
  // For the ellipse case this is a torus-ish flat ring; for the
  // capsule-chain we just stream points along each capsule's outer
  // edge later (the boundary particles already handle that shape).
  // We still keep a slightly-taller shimmer cylinder at very low
  // alpha so the boundary has 3D presence without reading as "wall".
  const shimmerMat = buildShimmerCylinderMaterial(boundaryUniforms);
  const shimmerMesh = new Mesh(
    new CylinderGeometry(1, 1, 3.2, 96, 1, true),
    shimmerMat,
  );
  shimmerMesh.scale.set(arenaRX, 1, arenaRZ);
  // Low shimmer — hint at a boundary without obscuring the scene.
  shimmerMesh.position.y = 1.6;
  root.add(shimmerMesh);

  // Thin glowing ring along the ellipse — draws just above the disc
  // so it reads as the visible edge of the chart. Capsule-chain
  // arenas lean on the particle ring for edge legibility (drawing
  // the exact perimeter of a capsule union as a mesh is a lot more
  // geometry for marginal visual gain).
  if (!hasCapsules) {
    const ringGeom = new RingGeometry(0.985, 1.015, 128, 1);
    const ringMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide,
      uniforms: {
        uTime: boundaryUniforms.uTime!,
        uColor: { value: accent.clone() },
      },
      vertexShader: /* glsl */ `
        void main() {
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float uTime;
        uniform vec3 uColor;
        void main() {
          float pulse = 0.65 + 0.35 * sin(uTime * 0.6);
          gl_FragColor = vec4(uColor * pulse, 0.85);
        }
      `,
    });
    const ringMesh = new Mesh(ringGeom, ringMat);
    ringMesh.rotation.x = -Math.PI / 2;
    ringMesh.scale.set(arenaRX, arenaRZ, 1);
    ringMesh.position.y = 0.05;
    root.add(ringMesh);
  }

  // Orbiting particle ring — procedural "dust" drifting around the
  // arena perimeter. Capsule arenas stream along the chain, ellipse
  // arenas orbit around the ellipse. Animated by game.ts each frame.
  const boundaryParticles = hasCapsules
    ? buildCapsuleBoundaryParticles(arenaSegments!, rng)
    : buildBoundaryParticles(arenaRX, arenaRZ, rng);
  root.add(boundaryParticles.group);

  // Gate rendering (v7). Each gate is a thin bar aligned with the
  // tangent of its (nx, nz) normal. game.ts writes uClosed each
  // frame using `gateIsClosed(gate, t)` with a short crossfade.
  const gateList: GateLike[] =
    (hole as unknown as { gates?: GateLike[] }).gates ?? [];
  const gates: GateRender[] = [];
  for (const g of gateList) {
    const render = buildGateRender(g, accent);
    root.add(render.root);
    gates.push(render);
  }

  // Wind streamers (v7). Legible visual for "there's an invisible
  // force pulling you" whenever windAccel is set on the hole.
  const windAccel = (hole as unknown as { windAccel?: { x: number; z: number } }).windAccel;
  let windStreamers: WindStreamers | null = null;
  if (windAccel && (Math.abs(windAccel.x) > 1e-4 || Math.abs(windAccel.z) > 1e-4)) {
    windStreamers = buildWindStreamers(
      arenaRX,
      arenaRZ,
      arenaSegments,
      windAccel,
      accent,
      rng,
    );
    root.add(windStreamers.points);
  }

  // Comet hazards (v11). Sweeping heads on straight tracks; game.ts
  // ticks position each frame using hole-local sim time so the visual
  // stays in lockstep with `cometPosAt` in physics.
  const cometList: CometLike[] =
    (hole as unknown as { comets?: CometLike[] }).comets ?? [];
  const comets: CometRender[] = [];
  for (const c of cometList) {
    const render = buildCometRender(c);
    root.add(render.head);
    root.add(render.track);
    comets.push(render);
  }

  return {
    root,
    ball,
    trail,
    trailGeom,
    aimGuide,
    aimGeom,
    trajectoryPreview,
    trajectoryGeom,
    powerRing,
    targetHalo,
    targetDisk,
    targetDiskMat,
    targetParticles,
    targetExtraMats,
    targetLensRing,
    planets,
    planetRoots,
    planetRings,
    gravityWells,
    wormholes,
    boundaryParticles,
    boundaryMat,
    gates,
    windStreamers,
    comets,
    dispose() {
      disposeObject3D(root);
    },
  };
}

// 60 Hz × 14s = 840 samples worst-case (MAX_SIM_SECONDS); 1024 buys
// headroom + clean power-of-two buffer alignment. Smaller values
// truncated long-distance drift shots so the trail stopped drawing
// while the ball was still moving on screen.
export const MAX_TRAIL_POINTS = 1024;
export const PREVIEW_SAMPLES = 48;

export interface GhostTrails {
  root: Group;
  dispose(): void;
  /** Tick once per frame with elapsed seconds to animate the Pro's Line pulse. */
  update?: (t: number) => void;
  /**
   * Re-position label sprites each frame to prevent pile-ups in screen space.
   * On early holes, most shots land in the hole and all their labels cluster
   * at the target — this stacks them vertically and collapses tall stacks.
   */
  declutter?: (camera: Camera) => void;
}

// Build translucent trails for recent shots on this hole. The "best" trail
// (lowest stroke count) is drawn gold; the rest are dim blue.
export function buildGhostTrails(
  shots: {
    handle?: string;
    samples: { x: number; y: number; z: number }[];
    strokes: number;
    outcome: string;
  }[],
): GhostTrails {
  const root = new Group();
  const disposables: { dispose(): void }[] = [];

  if (shots.length === 0) {
    return { root, dispose: () => void 0, update: undefined };
  }

  // The pulse that races along the Pro's Line — mutated each frame via update().
  let proPulse: Sprite | null = null;
  let proPulseSamples: { x: number; y: number; z: number }[] | null = null;

  // Label sprites (with their intended world-space anchors) so we can
  // reshuffle them each frame to avoid the "hole-in-one pile-up" on H1-3.
  const labelEntries: {
    sprite: Sprite;
    baseY: number;
    scale: number;
    rank: number;
  }[] = [];

  // Pro's Line: the successful shot with the fewest strokes.
  let bestIdx = -1;
  for (let i = 0; i < shots.length; i++) {
    const s = shots[i]!;
    if (s.outcome !== "holed") continue;
    if (bestIdx < 0 || s.strokes < shots[bestIdx]!.strokes) bestIdx = i;
  }

  for (let i = 0; i < shots.length; i++) {
    const s = shots[i]!;
    if (s.samples.length < 2) continue;
    const isBest = i === bestIdx;
    const isHoled = s.outcome === "holed";
    const geom = new BufferGeometry();
    const pts = new Float32Array(s.samples.length * 3);
    for (let j = 0; j < s.samples.length; j++) {
      pts[j * 3] = s.samples[j]!.x;
      pts[j * 3 + 1] = s.samples[j]!.y ?? 0.06;
      pts[j * 3 + 2] = s.samples[j]!.z;
    }
    geom.setAttribute("position", new Float32BufferAttribute(pts, 3));
    const mat = new LineBasicMaterial({
      color: isBest
        ? new Color("#ffd66a")
        : isHoled
          ? new Color("#6ab8ff")
          : new Color("#5577aa"),
      transparent: true,
      opacity: isBest ? 0.85 : 0.18,
      blending: AdditiveBlending,
      depthWrite: false,
    });
    const line = new Line(geom, mat);

    line.userData = { handle: s.handle };
    
    // Name label
    const canvas = document.createElement("canvas");
    canvas.width = 512;
    canvas.height = 128;
    const ctx = canvas.getContext("2d")!;
    
    ctx.fillStyle = "rgba(10, 15, 30, 0.75)";
    ctx.beginPath();
    ctx.roundRect(128, 32, 256, 64, 32);
    ctx.fill();
    
    ctx.strokeStyle = isBest ? "#ffd66a" : (isHoled ? "#6ab8ff" : "#88aacc");
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.fillStyle = isBest ? "#ffd66a" : (isHoled ? "#6ab8ff" : "#88aacc");
    ctx.font = "bold 32px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.shadowColor = "#000";
    ctx.shadowBlur = 8;
    
    let prefix = "";
    if (i === 0) prefix = "👑 ";
    else if (i === 1) prefix = "🥈 ";
    else if (i === 2) prefix = "🥉 ";

    ctx.fillText(`${prefix}${s.handle || "Ghost"}`, 256, 64);
    
    const tex = new CanvasTexture(canvas);
    const spriteMat = new SpriteMaterial({ map: tex, depthTest: false, transparent: true, opacity: isBest ? 0.95 : 0.6 });
    const sprite = new Sprite(spriteMat);
    sprite.userData = { handle: s.handle, rank: i, clusterFade: 1 };

    // Scale up for top ranks
    const scaleMultiplier = i === 0 ? 1.4 : (i === 1 ? 1.2 : (i === 2 ? 1.1 : 1.0));

    // Place roughly halfway
    const midIdx = Math.floor(s.samples.length / 2);
    const midP = s.samples[midIdx]!;
    const baseY = (midP.y ?? 0.06) + 1.2 * scaleMultiplier;
    sprite.position.set(midP.x, baseY, midP.z);
    sprite.scale.set(12 * scaleMultiplier, 3 * scaleMultiplier, 1);
    line.add(sprite);
    labelEntries.push({ sprite, baseY, scale: scaleMultiplier, rank: i });

    root.add(line);
    disposables.push(geom, mat, tex, spriteMat);

    // Gold pulse that races along the Pro's Line. Makes the day's best shot
    // feel like a celebrity — your eye follows it forward on every loop.
    if (isBest && !proPulse) {
      const pulseTex = new CanvasTexture(makePulseCanvas());
      const pulseMat = new SpriteMaterial({
        map: pulseTex,
        transparent: true,
        blending: AdditiveBlending,
        depthWrite: false,
        depthTest: true,
      });
      const pulse = new Sprite(pulseMat);
      pulse.scale.set(0.95, 0.95, 1);
      // Seed at the tee so the first frame is correct.
      const first = s.samples[0]!;
      pulse.position.set(first.x, (first.y ?? 0.06) + 0.25, first.z);
      root.add(pulse);
      disposables.push(pulseTex, pulseMat);
      proPulse = pulse;
      proPulseSamples = s.samples;
    }
  }

  const update: ((t: number) => void) | undefined =
    proPulse && proPulseSamples
      ? (t: number) => {
          const samples = proPulseSamples!;
          const pulse = proPulse!;
          // ~2.6s per sweep; short gap at the end before the next loop kicks.
          const cycle = 2.6;
          const u = (t % cycle) / cycle;
          // Linger-phase past 1.0 fades out and hides the pulse until the
          // next loop, so it doesn't just teleport back to the tee.
          const sweepEnd = 0.85;
          if (u > sweepEnd) {
            const mat = pulse.material as SpriteMaterial;
            mat.opacity = 0;
            pulse.visible = false;
            return;
          }
          pulse.visible = true;
          const v = u / sweepEnd; // 0..1 across the sweep
          const fIdx = v * (samples.length - 1);
          const idx = Math.min(samples.length - 2, Math.floor(fIdx));
          const frac = fIdx - idx;
          const a = samples[idx]!;
          const b = samples[idx + 1]!;
          pulse.position.set(
            a.x + (b.x - a.x) * frac,
            (a.y ?? 0.06) + ((b.y ?? 0.06) - (a.y ?? 0.06)) * frac + 0.25,
            a.z + (b.z - a.z) * frac,
          );
          // Fade in at start, fade out at end, steady in the middle.
          const edge = 0.08;
          const fade =
            v < edge ? v / edge : v > 1 - edge ? (1 - v) / edge : 1;
          const mat = pulse.material as SpriteMaterial;
          mat.opacity = 0.95 * fade;
        }
      : undefined;

  const declutter: ((camera: Camera) => void) | undefined =
    labelEntries.length > 1
      ? (camera: Camera) => {
          // Project every label anchor to NDC, then greedy-cluster by
          // screen proximity. Higher-ranked labels (i=0 first) win the base
          // slot; later ones stack vertically above the cluster. Once a
          // cluster reaches 4+, the overflow is hidden so the jumble clears.
          const projected = labelEntries.map((e) => {
            const v = new Vector3(
              e.sprite.position.x,
              e.baseY,
              e.sprite.position.z,
            );
            v.project(camera);
            return { entry: e, ndc: v, stack: 0, cluster: -1, size: 1 };
          });
          // X-threshold ~ pill width in NDC, Y-threshold ~ pill height.
          // Scaled by sprite scale so bigger labels claim more room.
          const clusterHeads: number[] = [];
          for (let i = 0; i < projected.length; i++) {
            const p = projected[i]!;
            if (p.ndc.z > 1 || p.ndc.z < -1) {
              // Behind camera / beyond far plane — hide.
              p.entry.sprite.visible = false;
              p.entry.sprite.userData.clusterFade = 0;
              continue;
            }
            let joined = -1;
            for (const headIdx of clusterHeads) {
              const h = projected[headIdx]!;
              const dx = Math.abs(p.ndc.x - h.ndc.x);
              const dy = Math.abs(p.ndc.y - h.ndc.y);
              const xThresh = 0.07 * Math.max(h.entry.scale, p.entry.scale);
              const yThresh = 0.025 * Math.max(h.entry.scale, p.entry.scale);
              if (dx < xThresh && dy < yThresh) {
                joined = headIdx;
                break;
              }
            }
            if (joined < 0) {
              p.cluster = i;
              p.stack = 0;
              clusterHeads.push(i);
            } else {
              const head = projected[joined]!;
              p.cluster = joined;
              head.size += 1;
              p.stack = head.size - 1;
            }
          }
          const maxStack = 2; // show head + 2 stacked neighbours
          for (const p of projected) {
            const e = p.entry;
            if (!e.sprite.visible && (p.ndc.z > 1 || p.ndc.z < -1)) continue;
            e.sprite.visible = true;
            if (p.stack === 0) {
              e.sprite.position.y = e.baseY;
              e.sprite.userData.clusterFade = 1;
            } else if (p.stack <= maxStack) {
              // Stack upward in world-space so labels read as a short column.
              e.sprite.position.y = e.baseY + p.stack * 2.2 * e.scale;
              e.sprite.userData.clusterFade = 1;
            } else {
              // Beyond the visible stack — hide, but keep the trail itself.
              e.sprite.visible = false;
              e.sprite.userData.clusterFade = 0;
            }
          }
        }
      : undefined;

  return {
    root,
    dispose: () => {
      for (const d of disposables) d.dispose();
    },
    update,
    declutter,
  };
}

/** A round, soft gold blob for the Pro's Line pulse. Pure radial gradient. */
function makePulseCanvas(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 128;
  c.height = 128;
  const ctx = c.getContext("2d")!;
  const grad = ctx.createRadialGradient(64, 64, 0, 64, 64, 64);
  grad.addColorStop(0, "rgba(255, 250, 220, 1)");
  grad.addColorStop(0.22, "rgba(255, 220, 120, 0.92)");
  grad.addColorStop(0.5, "rgba(255, 170, 60, 0.45)");
  grad.addColorStop(1, "rgba(255, 140, 0, 0)");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 128, 128);
  return c;
}

export function updateTrail(
  visuals: HoleVisuals,
  samples: { x: number; y: number; z: number }[],
  count: number,
): void {
  const posAttr = visuals.trailGeom.getAttribute(
    "position",
  ) as Float32BufferAttribute;
  const colAttr = visuals.trailGeom.getAttribute(
    "color",
  ) as Float32BufferAttribute;
  const n = Math.min(count, MAX_TRAIL_POINTS);
  // Color ramp — fade from bright cyan at the front to deep purple-blue at the back,
  // so the tail feels like it's burning out in vacuum.
  const headColor = new Color("#a9ffff");
  const midColor = new Color("#7cf5ff");
  const tailColor = new Color("#3b2cff");
  for (let i = 0; i < n; i++) {
    const s = samples[i]!;
    posAttr.setXYZ(i, s.x, s.y ?? 0.18, s.z);
    const u = n <= 1 ? 1 : i / (n - 1); // 0 = tail, 1 = head
    const c =
      u < 0.5
        ? tailColor.clone().lerp(midColor, u * 2)
        : midColor.clone().lerp(headColor, (u - 0.5) * 2);
    colAttr.setXYZ(i, c.r, c.g, c.b);
  }
  posAttr.needsUpdate = true;
  colAttr.needsUpdate = true;
  visuals.trailGeom.setDrawRange(0, n);
  visuals.trailGeom.computeBoundingSphere();
}

export function setAimGuide(
  visuals: HoleVisuals,
  from: Vector3,
  to: Vector3 | null,
): void {
  const attr = visuals.aimGeom.getAttribute(
    "position",
  ) as Float32BufferAttribute;
  attr.setXYZ(0, from.x, 0.22, from.z);
  if (to) {
    attr.setXYZ(1, to.x, 0.22, to.z);
    (visuals.aimGuide.material as LineBasicMaterial).opacity = 0.85;
  } else {
    attr.setXYZ(1, from.x, 0.22, from.z);
    (visuals.aimGuide.material as LineBasicMaterial).opacity = 0;
  }
  attr.needsUpdate = true;
}

// Forward-simulate a short preview of the ball's path with the chosen aim
// vector. Uses the same math as shared/physics.ts but cheaper — 120Hz steps,
// no collisions, 0.9s horizon. Just a hint to the player.
export function setTrajectoryPreview(
  visuals: HoleVisuals,
  hole: HoleLayout,
  from: { x: number; z: number },
  aim: { aimX: number; aimZ: number; power: number } | null,
): void {
  const mesh = visuals.trajectoryPreview;
  const mat = mesh.material as MeshBasicMaterial;
  if (!aim || aim.power <= 0.02) {
    mat.opacity = 0;
    mesh.count = 0;
    return;
  }
  const GRAVITY_SOFT = 0.35;
  const MAX_ACCEL = 220;
  const DT = 1 / 120;
  const STEPS = 108; // 0.9 seconds
  // v6: pull wind drift once so the preview honestly reflects the
  // solar-wind holes (H7+). Matches the shared/physics.ts hoisted const.
  const windAx = hole.windAccel?.x ?? 0;
  const windAz = hole.windAccel?.z ?? 0;

  let { vx, vz } = launchVelocity(aim);
  const powerScale = (hole as { powerScale?: number }).powerScale ?? 1;
  vx *= powerScale;
  vz *= powerScale;

  let x = from.x;
  let z = from.z;
  let sampleCount = 0;
  const dummy = new Matrix4();
  const maxSamples = mesh.instanceMatrix.count;

  for (let step = 0; step < STEPS; step++) {
    let ax = windAx;
    let az = windAz;

    for (let i = 0; i < hole.planets.length; i++) {
      const p = hole.planets[i]!;
      const dx = p.cx - x;
      const dz = p.cz - z;
      // Same repulsor softening as the server sim so the preview doesn't
      // spike when the line grazes a cool-blue (negative-strength) planet.
      const soft = p.strength < 0
        ? Math.max(GRAVITY_SOFT, p.radius * 0.5)
        : GRAVITY_SOFT;
      const r2 = dx * dx + dz * dz + soft * soft;
      const r = Math.sqrt(r2);
      const f = p.strength / r2;
      ax += (dx / r) * f;
      az += (dz / r) * f;
    }
    const amag = Math.hypot(ax, az);
    if (amag > MAX_ACCEL) {
      const k = MAX_ACCEL / amag;
      ax *= k;
      az *= k;
    }
    vx += ax * DT;
    vz += az * DT;
    x += vx * DT;
    z += vz * DT;
    
    // Check collisions
    let hit = false;
    for (let i = 0; i < hole.planets.length; i++) {
      const p = hole.planets[i]!;
      const d2 = (x - p.cx) ** 2 + (z - p.cz) ** 2;
      const minDist = p.radius + BALL_RADIUS;
      if (d2 < minDist * minDist) {
        hit = true;
        break;
      }
    }
    if (hit) break;

    // We only sample a subset of frames to make a dotted line
    if (step % 2 === 0 && sampleCount < maxSamples) {
      dummy.setPosition(x, 0.18, z);
      mesh.setMatrixAt(sampleCount, dummy);
      
      // Color fading out
      const alpha = 1 - sampleCount / (STEPS / 2);
      mesh.setColorAt(sampleCount, new Color(0.4 * alpha, 1.0 * alpha, 1.0 * alpha));
      sampleCount++;
    }
  }
  
  mat.opacity = 1;
  mesh.count = sampleCount;
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
}

export function setPowerRing(
  visuals: HoleVisuals,
  ballPos: Vector3,
  power: number,
): void {
  visuals.powerRing.position.set(ballPos.x, ballPos.y + 0.02, ballPos.z);
  const scale = 1 + power * 1.6;
  visuals.powerRing.scale.set(scale, scale, scale);
  (visuals.powerRing.material as MeshBasicMaterial).opacity = power * 0.9;
}

// ---------- builders ----------

interface PlanetBuild {
  root: Group;
  body: Mesh;
  ring?: Mesh;
}

// Rough mobile detection for particle-count budgets. Kept local so the
// render module doesn't need to accept SceneCtx on every builder call.
const isMobile = typeof window !== "undefined"
  && typeof window.matchMedia === "function"
  && window.matchMedia("(pointer: coarse)").matches;

// The orbital-chart shader: gradient disc + static concentric rings +
// proximity grid. Clipped to ellipse OR union-of-capsules in the
// fragment shader so the grid is visible *wherever* the surface is
// visible — the Day 7 hotwash #8 fix. `uBallPos` is shared with the
// shimmer cylinder so a single frame-loop write drives both.
function buildOrbitalChartMaterial(
  uniforms: Record<string, { value: unknown }>,
  // When true, produces the shimmer-cylinder variant (very faint,
  // doesn't draw rings). When false, the main disc.
  isShimmer: boolean,
): ShaderMaterial {
  const RINGS = isMobile ? 4 : 6; // concentric ring markings
  // NormalBlending on the disc keeps the pixels additive-ish in dark
  // areas without stacking over the bloom pass. We want the chart to
  // read as a surface, not a halo.
  return new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: NormalBlending,
    uniforms,
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPos = worldPosition.xyz;
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vWorldPos;
      uniform float uTime;
      uniform vec3 uBallPos;
      uniform vec3 uAccent;
      uniform float uArenaRX;
      uniform float uArenaRZ;
      uniform int uNumCaps;
      uniform vec4 uCapsA[${MAX_CAPSULES_IN_SHADER}];
      uniform vec4 uCapsB[${MAX_CAPSULES_IN_SHADER}];
      uniform float uUseCaps;

      // Distance from point p to line segment (a,b), xz only.
      float sdSegment(vec2 p, vec2 a, vec2 b) {
        vec2 pa = p - a;
        vec2 ba = b - a;
        float h = clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0);
        return length(pa - ba * h);
      }

      // Capsule-chain SDF: min distance to every segment, minus radius.
      // Negative inside the union, positive outside.
      float sdfCapsuleChain(vec2 p) {
        float best = 1e9;
        for (int i = 0; i < ${MAX_CAPSULES_IN_SHADER}; i++) {
          if (i >= uNumCaps) break;
          vec4 A = uCapsA[i];
          vec4 B = uCapsB[i];
          float d = sdSegment(p, A.xy, A.zw) - B.x;
          best = min(best, d);
        }
        return best;
      }

      // Ellipse SDF (positive outside, negative inside).
      float sdfEllipse(vec2 p, vec2 r) {
        float q = (p.x * p.x) / (r.x * r.x) + (p.y * p.y) / (r.y * r.y);
        return sqrt(q) - 1.0;
      }

      void main() {
        vec2 xz = vWorldPos.xz;

        // Arena SDF: pick capsule or ellipse based on uUseCaps.
        float sdEll = sdfEllipse(xz, vec2(uArenaRX, uArenaRZ));
        float sdCap = sdfCapsuleChain(xz);
        // Clip: only draw where we're *inside* the arena shape.
        float sd = mix(sdEll, sdCap, uUseCaps);
        if (sd > 0.0) discard;

        // Distance from edge (positive inside). Used for rim fades.
        float edgeDist = -sd;
        float edgeFade = smoothstep(0.0, 4.0, edgeDist);

        // Base translucent gradient — brighter near center of arena,
        // fading to clear at the edge. For the capsule arena the
        // "center" is the chain axis, which edgeDist already captures.
        float base = edgeFade * 0.14; // inner alpha ~0.10-0.14

        ${isShimmer
          ? `
        // Shimmer mode: just alpha × accent with a slow flicker, very low.
        float flick = 0.6 + 0.4 * sin(uTime * 0.4 + vWorldPos.y * 0.6);
        gl_FragColor = vec4(uAccent * 0.8, min(0.05, base * 0.3) * flick);
        return;
          `
          : `
        // Concentric rings — static, drawn on the disc only. Rings use
        // the ellipse radius (or capsule "near" distance); we tint bands
        // near every integer multiple of (minRadius / RINGS).
        float minR = min(uArenaRX, uArenaRZ);
        // Ring distance — for ellipse, normalized polar distance; for
        // capsule, we reuse edgeDist which reads as "how deep inside".
        float ringD = mix(length(xz / vec2(uArenaRX, uArenaRZ)) * minR, edgeDist, uUseCaps);
        float ringCycle = float(${RINGS}) / max(minR, 1.0);
        float ringBand = fract(ringD * ringCycle);
        float ringGlow = smoothstep(0.96, 1.0, ringBand) + smoothstep(0.04, 0.0, ringBand);
        ringGlow *= 0.25 * edgeFade;

        // Proximity grid — fragment-local, so it shows up *everywhere*
        // the disc draws. Squared cells tiled on worldPos, falloff from
        // ball position with a ~8-unit reach.
        vec2 fw = fract(xz * 1.0);
        float cellLine = step(0.93, fw.x) + step(0.93, fw.y);
        cellLine = clamp(cellLine, 0.0, 1.0);
        float ballDist = length(vec3(xz.x, 0.0, xz.y) - vec3(uBallPos.x, 0.0, uBallPos.z));
        float grid = cellLine * smoothstep(8.0, 1.2, ballDist) * 0.45 * edgeFade;

        vec3 col = uAccent;
        float alpha = base + ringGlow + grid;
        if (alpha < 0.01) discard;
        gl_FragColor = vec4(col, alpha);
          `}
      }
    `,
  });
}

// The shimmer cylinder — a very faint (alpha ≤ 0.05) tall-ish shell
// replacing the old opaque boundary wall. 3D presence only; the real
// edge legibility comes from the boundary ring + particles.
function buildShimmerCylinderMaterial(
  uniforms: Record<string, { value: unknown }>,
): ShaderMaterial {
  return new ShaderMaterial({
    side: BackSide,
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms,
    vertexShader: /* glsl */ `
      varying vec3 vWorldPos;
      varying float vY01;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPos = worldPosition.xyz;
        // y / localHeight so the fade top→bottom is simple in the frag.
        vY01 = clamp(position.y / 1.6 + 0.5, 0.0, 1.0);
        gl_Position = projectionMatrix * viewMatrix * worldPosition;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vWorldPos;
      varying float vY01;
      uniform float uTime;
      uniform vec3 uAccent;
      void main() {
        // Vertical fade so the top of the shimmer dissolves into vacuum.
        float fade = smoothstep(1.0, 0.2, vY01);
        float flick = 0.6 + 0.4 * sin(uTime * 0.6 + vWorldPos.x * 0.08 + vWorldPos.z * 0.08);
        // Alpha ≤ 0.05 keeps this a ghost, not a wall.
        float alpha = fade * 0.05 * flick;
        gl_FragColor = vec4(uAccent, alpha);
      }
    `,
  });
}

// One pulsing gate bar. The bar is a flat box aligned perpendicular
// to the gate's (nx, nz) normal — i.e. along the tangent (-nz, nx).
// Opacity + emissive crossfade with uClosed; game.ts computes and
// writes that value each frame via `gateIsClosed(gate, t)` with a
// short smoothstep so transitions don't pop.
function buildGateRender(gate: GateLike, accent: Color): GateRender {
  const group = new Group();
  group.position.set(gate.cx, 0.3, gate.cz);

  // rotation.y = -atan2(nz, nx) maps local +X onto world normal (nx, nz)
  // and local +Z onto world tangent (-nz, nx). The physics collider has
  // `thickness` along the normal and `halfWidth*2` along the tangent, so
  // the BoxGeometry dims are in local (X=normal, Y=up, Z=tangent) order
  // to match. Keeping this in sync is what makes the neon bar land where
  // the ball actually bounces.
  const angle = Math.atan2(gate.nz, gate.nx);
  group.rotation.y = -angle;

  const barGeom = new BoxGeometry(gate.thickness, 0.6, gate.halfWidth * 2);
  const barMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uClosed: { value: 1 },
      // Predictability glow — intensifies in the last 300ms before
      // closing. game.ts writes this 0..1 each frame.
      uLeadingEdge: { value: 0 },
      uColor: { value: accent.clone() },
    },
    vertexShader: /* glsl */ `
      varying vec3 vLocal;
      void main() {
        vLocal = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vLocal;
      uniform float uTime;
      uniform float uClosed;
      uniform float uLeadingEdge;
      uniform vec3 uColor;
      void main() {
        // Opacity + brightness ramps with closed state. Open ≈ 30% alpha.
        float alpha = mix(0.3, 0.95, uClosed);
        float emissive = mix(0.55, 1.8, uClosed);
        // Leading-edge glow stripe along the "top" of the box
        // (positive y in local coords) — wraps with uv-like band.
        float edge = smoothstep(0.25, 0.3, vLocal.y / 0.3);
        float pulse = 0.5 + 0.5 * sin(uTime * 6.0);
        // As leading-edge approaches 1 (last 300ms before close),
        // stripe becomes strongly pulsing, signalling imminent shut.
        float stripe = edge * (0.4 + uLeadingEdge * (0.6 + 0.4 * pulse));
        vec3 col = uColor * emissive + vec3(stripe);
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });
  const bar = new Mesh(barGeom, barMat);
  group.add(bar);

  return {
    gate,
    root: group,
    barMat,
    closedCrossfade: 1,
  };
}

// Comet hazard visual: a menacing glowing head with a spinning spike
// burst + outer halo, sweeping along a dashed-bright patrol line.
// Angrier red/magenta palette so the danger reads even on warm
// biome backgrounds, and the spike billboard spins so it never looks
// static. Track line broadcasts the hazard corridor — surprises from
// an invisible resetter are frustrating.
function buildCometRender(c: CometLike): CometRender {
  // Static patrol track — bright red-orange line between endpoints.
  const trackGeom = new BufferGeometry();
  const trackPts = new Float32Array([
    c.ax, 0.12, c.az,
    c.bx, 0.12, c.bz,
  ]);
  trackGeom.setAttribute("position", new Float32BufferAttribute(trackPts, 3));
  const trackMat = new LineBasicMaterial({
    color: new Color("#ff3a5e"),
    transparent: true,
    opacity: 0.6,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  const track = new Line(trackGeom, trackMat);

  const headGroup = new Group();
  headGroup.position.set(c.ax, 0.9, c.az);

  // Molten core — hot-white center fading to blood-red rim.
  const headCoreGeom = new SphereGeometry(c.radius * 1.35, 20, 16);
  const headMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      uniform float uTime;
      void main() {
        float f = pow(1.0 - max(dot(vEye, vNormal), 0.0), 1.3);
        // Fast nervous pulse — the head looks like it could hit you
        // right now, not in a minute.
        float pulse = 0.8 + 0.2 * sin(uTime * 10.0);
        vec3 core = vec3(1.0, 0.94, 0.72);
        vec3 edge = vec3(1.0, 0.16, 0.22);
        vec3 col = mix(core, edge, f) * pulse * 1.25;
        gl_FragColor = vec4(col, 0.95);
      }
    `,
  });
  const headCore = new Mesh(headCoreGeom, headMat);
  headGroup.add(headCore);

  // Star-burst spikes — a canvas sprite of radial rays that spins
  // around the head. Pure visual theatrics; reads unmistakably as
  // "danger" without costing additional geometry.
  const spikesTex = new CanvasTexture(makeCometSpikesCanvas());
  const spikesMat = new SpriteMaterial({
    map: spikesTex,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    opacity: 0.9,
  });
  const spikes = new Sprite(spikesMat);
  const spikeScale = c.radius * 4.2;
  spikes.scale.set(spikeScale, spikeScale, 1);
  // Store rotation rate on userData so game.ts can spin it per frame.
  spikes.userData.spinRate = 1.8;
  headGroup.add(spikes);

  // Outer halo — bigger red-magenta sphere with a rim shader.
  const haloGeom = new SphereGeometry(c.radius * 2.8, 20, 16);
  const haloMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: { uTime: { value: 0 } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      uniform float uTime;
      void main() {
        float f = pow(1.0 - max(dot(vEye, vNormal), 0.0), 2.6);
        float pulse = 0.85 + 0.15 * sin(uTime * 8.0);
        vec3 col = vec3(1.0, 0.24, 0.42) * pulse;
        gl_FragColor = vec4(col * f, f * 0.55);
      }
    `,
  });
  const halo = new Mesh(haloGeom, haloMat);
  headGroup.add(halo);

  // Record extra materials on the group so game.ts can tick uTime on
  // both shader materials each frame (headMat is returned directly).
  (headGroup.userData as { extras?: ShaderMaterial[] }).extras = [haloMat];

  return { comet: c, head: headGroup, headMat, track };
}

// Radial spike sprite — 8 tapered rays fading outward. Drawn once
// per session into a CanvasTexture; reused across all comets.
function makeCometSpikesCanvas(): HTMLCanvasElement {
  const size = 256;
  const cv = document.createElement("canvas");
  cv.width = size;
  cv.height = size;
  const ctx = cv.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  ctx.translate(size / 2, size / 2);
  const rays = 8;
  for (let i = 0; i < rays; i++) {
    ctx.save();
    ctx.rotate((i / rays) * Math.PI * 2);
    const grad = ctx.createLinearGradient(0, 0, size / 2, 0);
    grad.addColorStop(0, "rgba(255, 240, 200, 0.0)");
    grad.addColorStop(0.25, "rgba(255, 200, 130, 0.6)");
    grad.addColorStop(0.55, "rgba(255, 90, 120, 0.8)");
    grad.addColorStop(1.0, "rgba(255, 40, 80, 0.0)");
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(size / 2, 6);
    ctx.lineTo(size / 2, -6);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  }
  // Central glow for body presence.
  const glow = ctx.createRadialGradient(0, 0, 0, 0, 0, 36);
  glow.addColorStop(0, "rgba(255, 240, 220, 0.85)");
  glow.addColorStop(1, "rgba(255, 60, 100, 0)");
  ctx.fillStyle = glow;
  ctx.fillRect(-size / 2, -size / 2, size, size);
  return cv;
}

// Wind streamer particle field. Each particle drifts in the direction
// of windAccel; on reaching the arena bounds it wraps to the opposite
// side. Makes invisible wind legible.
function buildWindStreamers(
  arenaRX: number,
  arenaRZ: number,
  arenaSegments: ArenaCapsuleSeg[] | undefined,
  windAccel: { x: number; z: number },
  accent: Color,
  rng: () => number,
): WindStreamers {
  const count = isMobile ? 50 : 120;
  const positions = new Float32Array(count * 3);
  const seeds = new Float32Array(count * 3);
  // Spread evenly inside bounds.
  const halfX = Math.max(
    arenaRX,
    ...(arenaSegments?.map((c) => Math.max(Math.abs(c.ax), Math.abs(c.bx)) + c.radius) ?? [0]),
  );
  const halfZ = Math.max(
    arenaRZ,
    ...(arenaSegments?.map((c) => Math.max(Math.abs(c.az), Math.abs(c.bz)) + c.radius) ?? [0]),
  );
  for (let i = 0; i < count; i++) {
    positions[i * 3] = (rng() - 0.5) * halfX * 2;
    positions[i * 3 + 1] = 0.25 + rng() * 1.2;
    positions[i * 3 + 2] = (rng() - 0.5) * halfZ * 2;
    seeds[i * 3] = rng();
    seeds[i * 3 + 1] = 0.4 + rng() * 0.6; // per-particle speed multiplier
    seeds[i * 3 + 2] = 0; // unused
  }
  const geom = new BufferGeometry();
  geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geom.setAttribute("seed", new Float32BufferAttribute(seeds, 3));

  // Wind magnitude drives visible drift speed; normalize so the shader
  // handles direction + magnitude separately.
  const windMag = Math.hypot(windAccel.x, windAccel.z) || 1;

  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uWind: { value: new Vector3(windAccel.x, 0, windAccel.z) },
      uWindMag: { value: windMag },
      uColor: { value: accent.clone() },
      uHalfX: { value: halfX },
      uHalfZ: { value: halfZ },
    },
    vertexShader: /* glsl */ `
      attribute vec3 seed;
      varying float vSpeed;
      uniform float uTime;
      uniform vec3 uWind;
      uniform float uHalfX;
      uniform float uHalfZ;
      void main() {
        // Position drift: integrate wind over time, but wrap within
        // the arena bounds so particles don't pile up on one side.
        float speed = seed.y;
        vec3 drift = uWind * (uTime * speed * 0.35);
        vec3 p = position + drift;
        // Wrap to -halfX..+halfX / -halfZ..+halfZ.
        p.x = mod(p.x + uHalfX, 2.0 * uHalfX) - uHalfX;
        p.z = mod(p.z + uHalfZ, 2.0 * uHalfZ) - uHalfZ;
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        gl_PointSize = min(10.0, 3.5 * (220.0 / -mv.z));
        gl_Position = projectionMatrix * mv;
        vSpeed = speed;
      }
    `,
    fragmentShader: /* glsl */ `
      varying float vSpeed;
      uniform vec3 uColor;
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        // Elongate along x-axis of the point to read as a streak.
        // gl_PointCoord doesn't give us velocity direction; the streak
        // feel is achieved by a directional falloff.
        float streak = smoothstep(0.5, 0.1, abs(d.y)) * smoothstep(0.55, 0.0, abs(d.x));
        float a = streak * 0.15 * (0.6 + 0.4 * vSpeed);
        if (a < 0.01) discard;
        gl_FragColor = vec4(uColor, a);
      }
    `,
  });
  const points = new Points(geom, mat);

  return {
    points,
    update(t: number): void {
      mat.uniforms.uTime!.value = t;
    },
  };
}

// Capsule-chain boundary particles. Instead of orbiting around an
// ellipse, particles stream along each capsule's axis and spread
// outward in radial offset, mimicking the existing dust feel for
// bean/wavy arenas.
function buildCapsuleBoundaryParticles(
  segments: ArenaCapsuleSeg[],
  rng: () => number,
): BoundaryParticles {
  const group = new Group();
  const theme = currentTheme();

  // Three concentric "layers" matching the ellipse version. Particle
  // count scales with total capsule arc length so huge endless wavy
  // rivers (perimeter ~8000u) still look populated. Capped to keep
  // GPU + buffer-upload cost bounded.
  const ringCount = 3;
  let totalArc = 0;
  for (const s of segments) {
    totalArc += Math.hypot(s.bx - s.ax, s.bz - s.az) + Math.PI * s.radius;
  }
  const densityPer = isMobile ? 0.035 : 0.07;
  const baseMin = isMobile ? 60 : 120;
  const baseMax = isMobile ? 200 : 420;
  const perRing = Math.max(
    baseMin,
    Math.min(baseMax, Math.round(totalArc * densityPer)),
  );
  for (let ring = 0; ring < ringCount; ring++) {
    const radialOffset = 0.4 + ring * 1.6;
    const positions = new Float32Array(perRing * 3);
    const seeds = new Float32Array(perRing * 3);
    // (segIndex, tAlongSeg, yOffset)
    const meta = new Float32Array(perRing * 4);

    for (let i = 0; i < perRing; i++) {
      // Distribute evenly across all segments by arc length — cheap
      // approximation: sample uniform segment index and t along it.
      const segIdx = Math.floor(rng() * segments.length);
      const seg = segments[segIdx]!;
      const tAlong = rng();
      const ax = seg.ax;
      const az = seg.az;
      const bx = seg.bx;
      const bz = seg.bz;
      const px = ax + (bx - ax) * tAlong;
      const pz = az + (bz - az) * tAlong;
      // Perpendicular outward direction.
      const segDx = bx - ax;
      const segDz = bz - az;
      const segLen = Math.hypot(segDx, segDz) || 1;
      // Two outward normals; pick one deterministically by particle.
      const sign = rng() < 0.5 ? 1 : -1;
      const nx = (-segDz / segLen) * sign;
      const nz = (segDx / segLen) * sign;
      const radOffset = seg.radius + radialOffset + (rng() - 0.5) * 1.2;
      const y = (rng() - 0.2) * 2.4;
      positions[i * 3] = px + nx * radOffset;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = pz + nz * radOffset;
      seeds[i * 3] = rng() * Math.PI * 2;
      seeds[i * 3 + 1] = 0;
      seeds[i * 3 + 2] = y;
      meta[i * 4] = segIdx;
      meta[i * 4 + 1] = tAlong;
      meta[i * 4 + 2] = nx;
      meta[i * 4 + 3] = nz;
    }

    const geom = new BufferGeometry();
    geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
    geom.setAttribute("seed", new Float32BufferAttribute(seeds, 3));

    const color = new Color(theme.dustPalette[ring % 3]!);
    const mat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uColor: { value: color },
        uSize: { value: 3.0 + ring * 0.6 },
      },
      vertexShader: /* glsl */ `
        attribute vec3 seed;
        varying float vA;
        uniform float uTime;
        uniform float uSize;
        void main() {
          // Small bob around the baked position — preserves the chain
          // silhouette while giving a drifting feel.
          vec3 p = position + vec3(
            sin(uTime * 0.3 + seed.x) * 0.35,
            sin(uTime * 0.4 + seed.x) * 0.25,
            cos(uTime * 0.32 + seed.x) * 0.35
          );
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_PointSize = min(7.0, uSize * (250.0 / -mv.z));
          gl_Position = projectionMatrix * mv;
          vA = 0.55 + 0.45 * sin(uTime * 1.4 + seed.x * 3.1);
        }
      `,
      fragmentShader: /* glsl */ `
        varying float vA;
        uniform vec3 uColor;
        void main() {
          vec2 d = gl_PointCoord - vec2(0.5);
          float r = length(d);
          float a = smoothstep(0.5, 0.0, r) * vA * 0.7;
          gl_FragColor = vec4(uColor, a);
        }
      `,
    });
    const points = new Points(geom, mat);
    group.add(points);
    // Silence unused — keep meta accessible in userData if we ever
    // want to do per-segment animation (not needed today).
    (points.userData as { meta?: Float32Array }).meta = meta;
  }

  return {
    group,
    update(t: number): void {
      group.traverse((child) => {
        if (child.type === "Points") {
          const mat = (child as Points).material as ShaderMaterial;
          if (mat.uniforms?.uTime) mat.uniforms.uTime.value = t;
        }
      });
    },
  };
}

// Orbiting dust ring at the arena boundary. Each particle is stored at
// (angle, radiusOffset, yOffset, angularVel) and its position recomputed
// every frame by update(t). Reads as drifting debris hanging in the
// "atmosphere" of the playfield — no hard wall visible, just a softly
// moving veil of sparkles.
function buildBoundaryParticles(
  arenaRX: number,
  arenaRZ: number,
  rng: () => number,
): BoundaryParticles {
  const group = new Group();
  const theme = currentTheme();

  const ringCount = 3; // three concentric layers — near / mid / far
  // Scale particles with the ellipse perimeter so big endless arenas
  // stay visually populated. Ramanujan's approximation for an ellipse.
  const h = ((arenaRX - arenaRZ) / (arenaRX + arenaRZ)) ** 2;
  const perimeter =
    Math.PI * (arenaRX + arenaRZ) * (1 + (3 * h) / (10 + Math.sqrt(4 - 3 * h)));
  const densityPer = isMobile ? 0.045 : 0.09;
  const baseMin = isMobile ? 100 : 180;
  const baseMax = isMobile ? 260 : 520;
  const particlesPerRing = Math.max(
    baseMin,
    Math.min(baseMax, Math.round(perimeter * densityPer)),
  );
  for (let ring = 0; ring < ringCount; ring++) {
    const offset = 0.4 + ring * 1.6; // distance past the wall
    const count = particlesPerRing;
    const positions = new Float32Array(count * 3);
    const seeds = new Float32Array(count * 3);
    const axes = new Float32Array(count * 3); // (rx, rz, unused) per particle
    for (let i = 0; i < count; i++) {
      const theta = rng() * Math.PI * 2;
      const rOff = (rng() - 0.5) * 1.2;
      const y = (rng() - 0.2) * 3.2;
      const rxi = arenaRX + offset + rOff;
      const rzi = arenaRZ + offset + rOff;
      seeds[i * 3] = theta;
      seeds[i * 3 + 1] = 0;
      seeds[i * 3 + 2] = y;
      axes[i * 3] = rxi;
      axes[i * 3 + 1] = rzi;
      axes[i * 3 + 2] = 0;
      positions[i * 3] = Math.cos(theta) * rxi;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = Math.sin(theta) * rzi;
    }
    const geom = new BufferGeometry();
    geom.setAttribute("position", new Float32BufferAttribute(positions, 3));
    geom.setAttribute("seed", new Float32BufferAttribute(seeds, 3));
    geom.setAttribute("axes", new Float32BufferAttribute(axes, 3));

    const angularVel = 0.03 + ring * 0.015; // outer rings drift slower
    const color = new Color(theme.dustPalette[ring % 3]!);
    const mat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: {
        uTime: { value: 0 },
        uAngVel: { value: angularVel },
        uColor: { value: color },
        uSize: { value: 3.0 + ring * 0.6 },
      },
      vertexShader: /* glsl */ `
        attribute vec3 seed;
        attribute vec3 axes;
        varying float vA;
        uniform float uTime;
        uniform float uAngVel;
        uniform float uSize;
        void main() {
          float theta = seed.x + uTime * uAngVel;
          vec3 p = vec3(cos(theta) * axes.x, seed.z + sin(uTime * 0.4 + seed.x) * 0.25, sin(theta) * axes.y);
          vec4 mv = modelViewMatrix * vec4(p, 1.0);
          gl_PointSize = min(7.0, uSize * (250.0 / -mv.z));
          gl_Position = projectionMatrix * mv;
          vA = 0.55 + 0.45 * sin(uTime * 1.4 + seed.x * 3.1);
        }
      `,
      fragmentShader: /* glsl */ `
        varying float vA;
        uniform vec3 uColor;
        void main() {
          vec2 d = gl_PointCoord - vec2(0.5);
          float r = length(d);
          float a = smoothstep(0.5, 0.0, r) * vA * 0.7;
          gl_FragColor = vec4(uColor, a);
        }
      `,
    });
    const points = new Points(geom, mat);
    group.add(points);
  }

  return {
    group,
    update(t: number): void {
      group.traverse((child) => {
        if (child.type === "Points") {
          const mat = (child as Points).material as ShaderMaterial;
          if (mat.uniforms?.uTime) mat.uniforms.uTime.value = t;
        }
      });
    },
  };
}

// Flat additive disk laid on the ground under a planet. Radius scales
// with √|strength| so visual reach matches the feel of the 1/r² force
// — same math for attractors and repulsors, sign-agnostic. Pulsing
// direction is flipped for repulsors (outward waves vs inward) and
// the palette shifts cool so the player reads push vs pull instantly.
// Dashed ring on the ground showing where a moving planet orbits, so
// the player can anticipate its sweep before launching. Drawn once
// per-hole-load; no per-frame animation (the planet itself is what
// moves). Tinted with the biome accent at low alpha.
function buildOrbitPathRing(p: Planet): Mesh {
  const accent = new Color(currentTheme().accent);
  const r = p.orbit!.radius;
  const ring = new RingGeometry(r - 0.18, r + 0.18, 96, 1);
  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    uniforms: {
      uColor: { value: accent.clone() },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec2 vUv;
      uniform vec3 uColor;
      void main() {
        // Dash pattern around the ring — fract on angular coord.
        float dash = step(0.5, fract(vUv.x * 28.0));
        gl_FragColor = vec4(uColor, 0.28 * dash);
      }
    `,
  });
  const mesh = new Mesh(ring, mat);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(p.cx, 0.015, p.cz);
  mesh.renderOrder = -1;
  return mesh;
}

function buildGravityWell(p: Planet): Mesh {
  const absStrength = Math.abs(p.strength);
  const reach = Math.sqrt(absStrength) * 0.55;
  const isRepulsor = p.strength < 0;
  const geom = new CircleGeometry(reach, 72);
  const mat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uStrength: { value: Math.min(1.4, absStrength / 180) },
      uRepulsor: { value: isRepulsor ? 1.0 : 0.0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform float uStrength;
      uniform float uRepulsor;
      varying vec2 vUv;
      void main() {
        vec2 center = vec2(0.5);
        float d = distance(vUv, center) * 2.0; // 0 at center, 1 at rim
        if (d > 1.0) discard;
        // Radial grooves pulse inward for attractors, outward for repulsors.
        float dir = mix(-1.8, 1.8, uRepulsor);
        float rings = 0.5 + 0.5 * sin(d * 18.0 + uTime * dir);
        float body = pow(1.0 - d, 1.6);
        float alpha = body * (0.18 + 0.20 * rings) * uStrength;
        // Warm orange→magenta for pull, cool cyan→blue for push.
        vec3 warm = mix(vec3(1.0, 0.65, 0.35), vec3(1.0, 0.35, 0.9), smoothstep(0.3, 1.0, d));
        vec3 cool = mix(vec3(0.35, 0.85, 1.0), vec3(0.4, 0.55, 1.0), smoothstep(0.3, 1.0, d));
        vec3 col = mix(warm, cool, uRepulsor);
        gl_FragColor = vec4(col, alpha);
      }
    `,
  });
  const mesh = new Mesh(geom, mat);
  mesh.rotation.x = -Math.PI / 2;
  // Match the planet body's simT=0 position so the gravity well reads
  // correctly for orbiting planets.
  const start = p.orbit
    ? {
        x: p.cx + Math.cos(p.orbit.phase) * p.orbit.radius,
        z: p.cz + Math.sin(p.orbit.phase) * p.orbit.radius,
      }
    : { x: p.cx, z: p.cz };
  mesh.position.set(start.x, 0.03, start.z);
  mesh.renderOrder = -1; // draw before planets so halos sit on top
  return mesh;
}

// A wormhole visual: dark core sphere + additive halo + flat accretion
// ring. Two wormholes on a hole share partner colors so the player can
// read "these two connect" at a glance. The core is a small sphere so
// ball-into-wormhole reads as "swallowed" before the teleport fires.
function buildWormhole(
  cx: number,
  cz: number,
  radius: number,
  ringColor: string,
  glowColor: string,
): Group {
  const g = new Group();
  g.position.set(cx, 0.02, cz);

  // Core — tiny dark sphere sitting on the ground.
  const core = new Mesh(
    new SphereGeometry(radius * 0.55, 24, 18),
    new MeshBasicMaterial({ color: "#0a0614" }),
  );
  core.position.y = radius * 0.55;
  g.add(core);

  // Flat accretion ring on the ground.
  const ringMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uColor: { value: new Color(ringColor) },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uColor;
      varying vec2 vUv;
      void main() {
        vec2 p = vUv - 0.5;
        float d = length(p) * 2.0;
        if (d > 1.0) discard;
        float band = 0.5 + 0.5 * sin(d * 22.0 - uTime * 2.6);
        float alpha = smoothstep(0.1, 0.35, d) * smoothstep(1.0, 0.75, d) * band;
        gl_FragColor = vec4(uColor, alpha * 0.85);
      }
    `,
  });
  const ring = new Mesh(new RingGeometry(radius * 0.6, radius * 2.4, 72, 1), ringMat);
  ring.rotation.x = -Math.PI / 2;
  ring.position.y = 0.04;
  g.add(ring);

  // Fresnel halo sphere so the wormhole reads from a distance.
  const haloMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: { uColor: { value: new Color(glowColor) } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform vec3 uColor;
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        float f = pow(1.0 - max(dot(vEye, vNormal), 0.0), 2.5);
        gl_FragColor = vec4(uColor * f * 1.2, f * 0.6);
      }
    `,
  });
  const halo = new Mesh(new SphereGeometry(radius * 1.3, 24, 18), haloMat);
  halo.position.y = radius * 0.55;
  g.add(halo);

  // Userdata so the frame loop can tick the accretion shader time.
  g.userData.ringMat = ringMat;
  return g;
}

function buildPlanet(p: Planet, rng: () => number, index: number): PlanetBuild {
  const group = new Group();
  // Orbiting planets start at their simT=0 position, not the orbit
  // center, so aim-phase physics and aim-phase visuals agree.
  const start = p.orbit
    ? {
        x: p.cx + Math.cos(p.orbit.phase) * p.orbit.radius,
        z: p.cz + Math.sin(p.orbit.phase) * p.orbit.radius,
      }
    : { x: p.cx, z: p.cz };
  group.position.set(start.x, 0, start.z);

  // If the planet carries its own visual seed (set by Cosmic Runner so
  // each planet keeps a stable identity across windowed-rebuilds),
  // use a planet-local RNG. Otherwise fall back to the shared hole-
  // level rng — preserving exact behaviour for daily / endless play.
  const localRng = p.seed != null ? mulberry32(p.seed) : rng;

  const theme = currentTheme();
  const type = Math.floor(localRng() * 4); // 0 gas-giant, 1 rocky, 2 icy, 3 volcanic
  const hueBase = (theme.planetHueStart + localRng() * theme.planetHueSpan) % 1;
  const sunDir = new Vector3(...theme.sunDir).normalize();

  // Higher detail on shader-driven types; slightly less on the rocky noise one
  // which has flatShading for the faceted rock look.
  const geom = new IcosahedronGeometry(p.radius, type === 1 ? 2 : 3);
  const material = buildPlanetShader(type, hueBase, sunDir);
  const mesh = new Mesh(geom, material);
  mesh.userData.spin = (localRng() - 0.5) * 0.35 + 0.05;
  mesh.userData.tilt = (localRng() - 0.5) * 0.6;
  mesh.rotation.z = mesh.userData.tilt;
  group.add(mesh);

  // Atmospheric halo — a larger additive sphere w/ fresnel falloff.
  // Repulsors (strength < 0) get a cool-blue inverted halo so the
  // player reads "this pushes, don't aim at it" at a glance, without
  // needing a label. The halo is also slightly larger so the outward
  // push is legible even from the 3rd-person camera angle.
  const isRepulsor = p.strength < 0;
  const haloScale = isRepulsor ? 1.35 + localRng() * 0.1 : 1.18 + localRng() * 0.1;
  const haloColor = isRepulsor
    ? new Color().setHSL(0.58, 0.9, 0.6) // icy-cyan
    : new Color().setHSL(
        type === 2 ? 0.57 : (hueBase + 0.15) % 1,
        type === 2 ? 0.6 : 0.55,
        0.6,
      );
  const haloMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
    uniforms: { uColor: { value: haloColor } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      uniform vec3 uColor;
      void main() {
        float f = pow(1.0 - max(dot(vEye, vNormal), 0.0), 3.5);
        gl_FragColor = vec4(uColor * f * 0.85, f * 0.55);
      }
    `,
  });
  const halo = new Mesh(new SphereGeometry(p.radius * haloScale, 32, 24), haloMat);
  group.add(halo);

  // Some planets get rings (gas giants more often, rocky rarely).
  let ring: Mesh | undefined;
  const wantsRing = type === 0 ? localRng() > 0.25 : localRng() > 0.75;
  if (wantsRing) {
    const inner = p.radius * (1.55 + localRng() * 0.2);
    const outer = inner + p.radius * (0.55 + localRng() * 0.45);
    const ringMat = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
      blending: AdditiveBlending,
      uniforms: {
        uInner: { value: inner },
        uOuter: { value: outer },
        uColor: { value: new Color().setHSL((hueBase + 0.1) % 1, 0.55, 0.6) },
      },
      vertexShader: /* glsl */ `
        varying float vR;
        void main() {
          vR = length(position.xy);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying float vR;
        uniform float uInner;
        uniform float uOuter;
        uniform vec3 uColor;
        void main() {
          float t = (vR - uInner) / max(uOuter - uInner, 1e-3);
          // 4-5 bands across the ring.
          float band = sin(t * 16.0) * 0.5 + 0.5;
          float alpha = smoothstep(0.0, 0.1, t) * smoothstep(1.0, 0.88, t);
          alpha *= 0.3 + 0.7 * band;
          gl_FragColor = vec4(uColor, alpha * 0.85);
        }
      `,
    });
    ring = new Mesh(new RingGeometry(inner, outer, 96, 1), ringMat);
    ring.rotation.x = -Math.PI / 2 + (localRng() - 0.5) * 0.4;
    ring.rotation.z = (localRng() - 0.5) * 0.3;
    group.add(ring);
  }

  // Silence unused index.
  void index;

  return { root: group, body: mesh, ring };
}

// Unified planet shader — all types share the same sun-lit Lambertian base
// with a terminator rim, and plug in a type-specific surface pattern.
function buildPlanetShader(
  type: number,
  hueBase: number,
  sunDir: Vector3,
): ShaderMaterial {
  // Palette picks per type.
  let paletteA: Color;
  let paletteB: Color;
  let emissive: Color;
  let surfaceFrag: string;

  if (type === 0) {
    // Gas giant — smooth bands.
    paletteA = new Color().setHSL(hueBase, 0.6, 0.58);
    paletteB = new Color().setHSL((hueBase + 0.08) % 1, 0.55, 0.36);
    emissive = paletteA.clone().multiplyScalar(0.08);
    surfaceFrag = /* glsl */ `
      float y = vLocal.y / max(length(vLocal), 1e-3);
      y += sin(vLocal.x * 0.8 + uTime * 0.12) * 0.06;
      float bands = sin(y * 18.0) * 0.5 + 0.5;
      bands = smoothstep(0.2, 0.8, bands);
      // Vortex patch — one big "storm" drifts across.
      float storm = smoothstep(0.85, 1.0, sin(vLocal.x * 1.2 + uTime * 0.2) * cos(vLocal.y * 1.5 - uTime * 0.18));
      vec3 surf = mix(uPaletteB, uPaletteA, bands);
      surf += storm * uPaletteA * 0.4;
      surface = surf;
    `;
  } else if (type === 1) {
    // Rocky — crater noise.
    paletteA = new Color().setHSL((hueBase * 0.3) % 1, 0.5, 0.48);
    paletteB = new Color().setHSL((hueBase * 0.3 + 0.05) % 1, 0.35, 0.26);
    emissive = paletteB.clone().multiplyScalar(0.06);
    surfaceFrag = /* glsl */ `
      vec3 p = vLocal * 1.8;
      float n = 0.0;
      float amp = 0.55;
      for (int i = 0; i < 4; i++) {
        n += amp * sin(p.x * 2.1 + sin(p.y * 1.7)) * cos(p.z * 1.9 + sin(p.x * 1.3));
        p *= 1.9;
        amp *= 0.5;
      }
      float craters = smoothstep(-0.3, 0.6, n);
      surface = mix(uPaletteB, uPaletteA, craters);
    `;
  } else if (type === 2) {
    // Icy — frost patches + white highlights.
    paletteA = new Color().setHSL(0.56, 0.18, 0.86);
    paletteB = new Color().setHSL(0.57, 0.45, 0.52);
    emissive = paletteA.clone().multiplyScalar(0.06);
    surfaceFrag = /* glsl */ `
      vec3 p = vLocal * 1.2;
      float n = sin(p.x * 3.0 + p.y * 2.0) + cos(p.z * 2.5 + p.y * 1.3);
      float frost = smoothstep(-0.6, 1.2, n);
      surface = mix(uPaletteB, uPaletteA, frost);
    `;
  } else {
    // Volcanic — dark crust with glowing cracks; emissive in the unlit regions.
    paletteA = new Color("#ffb065"); // glowing lava
    paletteB = new Color("#180a14"); // dark rock
    emissive = new Color("#ff7030");
    surfaceFrag = /* glsl */ `
      vec3 p = vLocal * 2.2;
      // Cell-ish pattern via nested sin waves — approximates magma cracks.
      float a = sin(p.x * 3.1 + sin(p.y * 2.0 + uTime * 0.3));
      float b = cos(p.z * 2.8 + sin(p.x * 2.2 - uTime * 0.2));
      float cracks = smoothstep(0.75, 1.0, a * b);
      // Pulse the crack glow.
      float pulse = 0.6 + 0.4 * sin(uTime * 0.8);
      surface = mix(uPaletteB, uPaletteA, cracks) + cracks * uEmissive * pulse;
    `;
  }

  const uniforms: Record<string, { value: unknown }> = {
    uTime: { value: 0 },
    uPaletteA: { value: paletteA },
    uPaletteB: { value: paletteB },
    uEmissive: { value: emissive },
    uSunDir: { value: sunDir.clone() },
    uType: { value: type },
  };

  return new ShaderMaterial({
    uniforms,
    vertexShader: /* glsl */ `
      varying vec3 vNormalWorld;
      varying vec3 vEye;
      varying vec3 vLocal;
      void main() {
        vLocal = position;
        vNormalWorld = normalize(mat3(modelMatrix) * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormalWorld;
      varying vec3 vEye;
      varying vec3 vLocal;
      uniform float uTime;
      uniform vec3 uPaletteA;
      uniform vec3 uPaletteB;
      uniform vec3 uEmissive;
      uniform vec3 uSunDir;
      uniform int uType;
      void main() {
        vec3 surface;
        ${surfaceFrag}

        // Lambertian from the virtual sun (world-space).
        vec3 s = normalize(uSunDir);
        float lambert = max(0.0, dot(vNormalWorld, s));
        // Soft ambient so the dark side doesn't crush.
        float ambient = 0.22;
        // Terminator rim — glow right at day/night boundary.
        float term = 1.0 - abs(dot(vNormalWorld, s));
        float rim = pow(term, 4.0) * 0.7;

        // Volcanic stays self-lit; others respect the sun.
        vec3 lit;
        if (uType == 3) {
          lit = surface + uEmissive * 0.2; // self-emissive already baked into surface
        } else {
          lit = surface * (ambient + lambert) + uEmissive;
        }

        // Edge fresnel — always on, sells the spherical form.
        float fres = pow(1.0 - max(dot(vEye, vNormalWorld), 0.0), 2.4);
        lit += fres * mix(uPaletteA, vec3(1.0), 0.5) * 0.55;
        lit += rim * uPaletteA;

        gl_FragColor = vec4(lit, 1.0);
      }
    `,
  });
}

function buildBlackHole(t: Target): {
  root: Group;
  halo: Mesh;
  disk: Mesh;
  diskMat: ShaderMaterial;
  particles: Points;
  /** Billboarded 360° lensing torus. game.ts calls `lensRing.lookAt(
   *  camera.position)` each frame so the ring's axis (local +Z)
   *  always points at the camera, rendering face-on as a full circle. */
  lensRing: Mesh;
  /** Extra shader materials that need `uTime` written each frame
   *  (photon ring, lens ring). Kept as a flat list so game.ts doesn't
   *  have to know the internal structure. */
  extraMats: ShaderMaterial[];
} {
  const group = new Group();
  group.position.set(t.cx, 0, t.cz);

  // Event-horizon sphere — pitch-black, matte, slightly oversized so the
  // bright photon ring appears to hug its silhouette.
  const coreGeom = new SphereGeometry(t.radius * 0.7, 48, 32);
  const coreMat = new MeshBasicMaterial({ color: new Color("#000000") });
  const core = new Mesh(coreGeom, coreMat);
  core.renderOrder = 1;
  group.add(core);

  // Photon ring — thin hot torus wrapping the horizon at its silhouette.
  // Slightly tilted like the accretion disk so the two read as a system.
  const photonRing = new Mesh(
    new TorusGeometry(t.radius * 0.78, t.radius * 0.04, 16, 128),
    new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      uniforms: { uTime: { value: 0 } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform float uTime;
        void main() {
          float flick = 0.82 + 0.18 * sin(uTime * 1.6 + vUv.x * 18.0);
          vec3 col = vec3(1.0, 0.88, 0.56) * flick;
          gl_FragColor = vec4(col, 0.95);
        }
      `,
    }),
  );
  photonRing.rotation.x = -Math.PI / 2 + 0.22;
  photonRing.renderOrder = 2;
  group.add(photonRing);

  // Target Beacon (visible from afar)
  const beaconGeom = new CylinderGeometry(0.8, 0.8, 120, 16);
  beaconGeom.translate(0, 60, 0);
  const beaconMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
    vertexShader: `
      varying float vY;
      void main() {
        vY = position.y / 120.0;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      varying float vY;
      void main() {
        float a = pow(1.0 - vY, 3.0);
        gl_FragColor = vec4(1.0, 0.55, 0.22, a * 0.38);
      }
    `
  });
  const beacon = new Mesh(beaconGeom, beaconMat);
  group.add(beacon);

  // Accretion disk — flat tilted ring with a swirling doppler-tinted
  // shader. Warmer hot-orange core fading to deep violet at the edges
  // so the ring reads like Interstellar rather than a blue sensor glow.
  const diskInner = t.radius * 0.82;
  const diskOuter = t.radius * 2.6;
  const diskMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    side: DoubleSide,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uInner: { value: diskInner },
      uOuter: { value: diskOuter },
    },
    vertexShader: /* glsl */ `
      varying vec2 vLocal;
      void main() {
        vLocal = position.xy;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec2 vLocal;
      uniform float uTime;
      uniform float uInner;
      uniform float uOuter;
      void main() {
        float r = length(vLocal);
        if (r < uInner || r > uOuter) discard;
        float theta = atan(vLocal.y, vLocal.x);
        float t = (r - uInner) / (uOuter - uInner);
        // Doppler-ish tint — one side (approaching) bluer, other (receding) redder.
        float dopp = sin(theta + uTime * 1.2) * 0.5 + 0.5;
        // Swirl bands that rotate faster on the inside (Keplerian-ish).
        float swirl = sin(theta * 6.0 + uTime * (3.0 + 6.0 * (1.0 - t))) * 0.5 + 0.5;
        vec3 hot    = vec3(1.00, 0.78, 0.38);
        vec3 white  = vec3(1.00, 0.96, 0.82);
        vec3 amber  = vec3(0.95, 0.45, 0.18);
        vec3 deep   = vec3(0.12, 0.02, 0.20);
        // Inner edge peaks bright-white, fades to amber then deep violet.
        vec3 base = mix(white, hot, smoothstep(0.0, 0.25, t));
        base = mix(base, amber, smoothstep(0.25, 0.65, t));
        base = mix(base, deep, smoothstep(0.65, 1.0, t));
        base = mix(base, base + vec3(0.10, 0.0, 0.0), dopp * 0.35);
        float alpha = smoothstep(0.0, 0.08, t) * smoothstep(1.0, 0.65, t);
        alpha *= 0.55 + 0.45 * swirl;
        gl_FragColor = vec4(base * (0.95 + 0.45 * swirl), alpha * 0.92);
      }
    `,
  });
  const disk = new Mesh(
    new RingGeometry(diskInner, diskOuter, 160, 1),
    diskMat,
  );
  disk.rotation.x = -Math.PI / 2 + 0.22; // tilt toward camera for drama
  disk.position.y = 0;
  group.add(disk);

  // Lensing ring — a FULL 360° torus that billboards to always face
  // the camera. Hugs the horizon silhouette (close to the core
  // radius of 0.7) so it reads as the actual photon ring around the
  // event horizon, not a separate accretion ring. Shares the palette
  // of the flat accretion disk (white → hot → amber → deep violet)
  // so the two read as one structure of gas. game.ts reorients this
  // each frame via lookAt(camera.position) so the ring's local +Z
  // axis always points at the camera and the ring renders face-on
  // as a full circle.
  const lensRingInner = t.radius * 0.82;
  const lensRingOuter = t.radius * 0.97;
  const lensRingMean  = (lensRingInner + lensRingOuter) / 2;
  const lensRingTube  = (lensRingOuter - lensRingInner) / 2;
  const lensRingGeom = new TorusGeometry(
    lensRingMean,
    lensRingTube,
    24,
    160,
  );
  const lensRingMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
    uniforms: {
      uTime: { value: 0 },
    },
    vertexShader: /* glsl */ `
      varying vec2 vUv;
      void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    // Fragment shader mirrors the flat disk's swirl + doppler
    // mixing (white/hot/amber/deep) so the vertical lensed ring and
    // the horizontal accretion disk read as one continuous gas
    // structure. vUv.x runs around the ring (= disk's `theta`);
    // vUv.y runs around the tube cross-section and stands in for
    // the disk's radial `t` — edges are the inner/outer rim of the
    // accretion, middle is the hottest band.
    fragmentShader: /* glsl */ `
      varying vec2 vUv;
      uniform float uTime;
      void main() {
        float theta = vUv.x * 6.2831853;
        // Collapse vUv.y (0..1 around the tube) to [0..1] "t" where
        // 0 is tube edge (inner/outer accretion rim) and 1 is the
        // centerline (hot band).
        float tt = 1.0 - abs(vUv.y - 0.5) * 2.0;
        float dopp  = sin(theta + uTime * 1.2) * 0.5 + 0.5;
        float swirl = sin(theta * 6.0 + uTime * (3.0 + 6.0 * (1.0 - tt))) * 0.5 + 0.5;
        vec3 hot    = vec3(1.00, 0.78, 0.38);
        vec3 white  = vec3(1.00, 0.96, 0.82);
        vec3 amber  = vec3(0.95, 0.45, 0.18);
        vec3 deep   = vec3(0.12, 0.02, 0.20);
        vec3 base = mix(white, hot, smoothstep(0.0, 0.25, tt));
        base = mix(base, amber, smoothstep(0.25, 0.65, 1.0 - tt));
        base = mix(base, deep, smoothstep(0.65, 1.0, 1.0 - tt));
        base = mix(base, base + vec3(0.10, 0.0, 0.0), dopp * 0.35);
        float alpha = smoothstep(0.0, 0.12, tt) * smoothstep(1.0, 0.65, 1.0 - tt);
        alpha *= 0.55 + 0.45 * swirl;
        gl_FragColor = vec4(base * (0.95 + 0.45 * swirl), alpha * 0.92);
      }
    `,
  });
  const lensRing = new Mesh(lensRingGeom, lensRingMat);
  lensRing.renderOrder = 3;
  group.add(lensRing);

  // Halo ring — soft warm glow on the ground plane that signals the
  // capture radius at a glance. Kept subtle so it doesn't fight with
  // the accretion disk.
  const halo = new Mesh(
    new RingGeometry(t.radius * 1.02, t.radius * 1.18, 64),
    new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: AdditiveBlending,
      side: DoubleSide,
      uniforms: { uTime: { value: 0 } },
      vertexShader: /* glsl */ `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        varying vec2 vUv;
        uniform float uTime;
        void main() {
          float pulse = 0.55 + 0.25 * sin(uTime * 2.0);
          vec3 col = mix(vec3(1.0, 0.62, 0.28), vec3(1.0, 0.88, 0.56), pulse);
          gl_FragColor = vec4(col, 0.38 + 0.12 * pulse);
        }
      `,
    }),
  );
  halo.rotation.x = -Math.PI / 2;
  halo.position.y = 0.04;
  halo.name = "halo";
  group.add(halo);

  // Inward-drawn particle ring — points orbiting/decaying inward.
  const particleCount = 220;
  const positions = new Float32Array(particleCount * 3);
  const seeds = new Float32Array(particleCount * 3);
  for (let i = 0; i < particleCount; i++) {
    const theta = Math.random() * Math.PI * 2;
    const r = diskInner + Math.random() * (diskOuter - diskInner);
    positions[i * 3] = Math.cos(theta) * r;
    positions[i * 3 + 1] = 0.05 + (Math.random() - 0.5) * 0.15;
    positions[i * 3 + 2] = Math.sin(theta) * r;
    seeds[i * 3] = theta;
    seeds[i * 3 + 1] = r;
    seeds[i * 3 + 2] = Math.random();
  }
  const pGeom = new BufferGeometry();
  pGeom.setAttribute("position", new Float32BufferAttribute(positions, 3));
  pGeom.setAttribute("seed", new Float32BufferAttribute(seeds, 3));
  const pMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    uniforms: {
      uTime: { value: 0 },
      uInner: { value: diskInner },
      uOuter: { value: diskOuter },
    },
    vertexShader: /* glsl */ `
      attribute vec3 seed;
      varying float vA;
      uniform float uTime;
      uniform float uInner;
      uniform float uOuter;
      void main() {
        float theta = seed.x + uTime * (1.8 + 1.2 * seed.z);
        // Decay from initial r down toward inner over a loop period.
        float decayPhase = fract(uTime * 0.1 + seed.z);
        float r = mix(seed.y, uInner, decayPhase);
        vec3 p = vec3(cos(theta) * r, position.y, sin(theta) * r);
        vec4 mv = modelViewMatrix * vec4(p, 1.0);
        // Clamp screen-space size: the previous formula let particles near
        // the camera blow up to 60+px sprites, and from the 3rd-person
        // low angle you'd see a vertical cascade of stacked haloes between
        // camera and target. 10px cap keeps them as fireflies, not flares.
        gl_PointSize = min(10.0, 3.0 * (300.0 / -mv.z));
        gl_Position = projectionMatrix * mv;
        vA = 1.0 - decayPhase;
      }
    `,
    fragmentShader: /* glsl */ `
      varying float vA;
      void main() {
        vec2 d = gl_PointCoord - vec2(0.5);
        float r = length(d);
        float a = smoothstep(0.5, 0.0, r) * vA;
        gl_FragColor = vec4(1.0, 0.85, 0.5, a);
      }
    `,
  });
  const particles = new Points(pGeom, pMat);
  group.add(particles);

  return {
    root: group,
    halo,
    disk,
    diskMat,
    particles,
    lensRing,
    extraMats: [
      photonRing.material as ShaderMaterial,
      lensRingMat,
    ],
  };
}

function buildTeeRing(x: number, z: number): Mesh {
  const ring = new Mesh(
    new RingGeometry(0.9, 1.05, 36),
    new MeshBasicMaterial({
      color: new Color("#9cff7c"),
      side: DoubleSide,
      transparent: true,
      opacity: 0.8,
      blending: AdditiveBlending,
      depthWrite: false,
    }),
  );
  ring.rotation.x = -Math.PI / 2;
  ring.position.set(x, 0.015, z);
  return ring;
}

function buildBall(): Mesh {
  const glow = new Color(currentTheme().ballGlow);
  const geom = new SphereGeometry(0.42, 32, 32);
  const mat = new ShaderMaterial({
    uniforms: {
      uTime: { value: 0 },
      uGlow: { value: glow.clone() },
    },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vPosition;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vPosition = mv.xyz;
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      uniform float uTime;
      uniform vec3 uGlow;
      varying vec3 vNormal;
      varying vec3 vPosition;
      void main() {
        vec3 viewDir = normalize(-vPosition);
        float fres = pow(1.0 - max(dot(viewDir, vNormal), 0.0), 2.0);
        float pulse = 0.9 + 0.1 * sin(uTime * 2.2);
        vec3 base = mix(vec3(0.1, 0.25, 0.45), uGlow, fres) * pulse;
        vec3 col = base + uGlow * fres * 1.2;
        gl_FragColor = vec4(col, 1.0);
      }
    `,
  });
  const body = new Mesh(geom, mat);

  // Outer glow sphere — additive shell, child of body so it follows every move.
  const glowGeom = new SphereGeometry(0.8, 24, 18);
  const glowMat = new ShaderMaterial({
    transparent: true,
    depthWrite: false,
    blending: AdditiveBlending,
    side: DoubleSide,
    uniforms: { uGlow: { value: glow.clone() } },
    vertexShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mv = modelViewMatrix * vec4(position, 1.0);
        vEye = normalize(-mv.xyz);
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */ `
      varying vec3 vNormal;
      varying vec3 vEye;
      uniform vec3 uGlow;
      void main() {
        float f = pow(1.0 - max(dot(vEye, vNormal), 0.0), 2.5);
        gl_FragColor = vec4(uGlow * f * 0.9, f * 0.55);
      }
    `,
  });
  const glowMesh = new Mesh(glowGeom, glowMat);
  body.add(glowMesh);

  return body;
}

// Legacy helper — no longer used but exported for compat.
export function buildPathTube(samples: Vector3[]): Mesh | null {
  if (samples.length < 2) return null;
  const curve = new CatmullRomCurve3(samples);
  const geom = new TubeGeometry(curve, samples.length * 2, 0.08, 6, false);
  const mat = new MeshBasicMaterial({
    color: new Color("#7cf5ff"),
    transparent: true,
    opacity: 0.7,
    blending: AdditiveBlending,
    depthWrite: false,
  });
  return new Mesh(geom, mat);
}

function disposeObject3D(obj: Object3D): void {
  obj.traverse((n) => {
    const m = n as Mesh;
    if (m.geometry) m.geometry.dispose();
    const mat = (m.material as unknown) as
      | MeshBasicMaterial
      | MeshStandardMaterial
      | ShaderMaterial
      | LineBasicMaterial
      | undefined;
    if (mat && "dispose" in mat) mat.dispose();
  });
}
